import { API_URL } from '../config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import DataTable from './common/DataTable';

interface SentEmailLog {
  _id: string;
  email: string;
  dateSent: string;
  templateId?: {
    _id: string;
    name: string;
    category: string;
  };
  templateType: string;
  templateCategory: string;
  recipientName: string;
  subject: string;
  content: string;
  status: 'sent' | 'delivered' | 'opened' | 'replied' | 'bounced';
  campaignId?: string;
  source: 'manual' | 'automated' | 'imported';
  senderName?: string;
  senderEmail?: string;
  metadata?: {
    platform?: string;
    username?: string;
    followersCount?: number;
    engagementRate?: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface EmailStats {
  total: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  replied: number;
  bounced: number;
  failed: number;
  deliveryRate: number;
  openRate: number;
  clickRate: number;
  replyRate: number;
  bounceRate: number;
}

const OutreachLogs: React.FC = () => {
  const navigate = useNavigate();
  const { ruleId } = useParams<{ ruleId: string }>();
  const [logs, setLogs] = useState<SentEmailLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filteredLogs, setFilteredLogs] = useState<SentEmailLog[]>([]);
  const [selectedLog, setSelectedLog] = useState<SentEmailLog | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const token = localStorage.getItem('authToken');

  // Fetch email logs
  const fetchEmailLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/email-rules/${ruleId || 'initial-email'}/logs?limit=500`,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );

      if (!response.ok) throw new Error('Failed to fetch logs');

      const data = await response.json();

      if (data.success) {
        setLogs(data.data || []);
        setFilteredLogs(data.data || []);
        setStats(data.stats || null);
      }
    } catch (error) {
      console.error('Error fetching email logs:', error);
      setLogs([]);
      setFilteredLogs([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmailLogs();
  }, [ruleId]);

  // Filter logs based on search
  useEffect(() => {
    if (!search) {
      setFilteredLogs(logs);
    } else {
      const searchLower = search.toLowerCase();
      const filtered = logs.filter(log => 
        log.email.toLowerCase().includes(searchLower) ||
        log.recipientName?.toLowerCase().includes(searchLower) ||
        log.subject?.toLowerCase().includes(searchLower) ||
        log.status?.toLowerCase().includes(searchLower) ||
        log.metadata?.platform?.toLowerCase().includes(searchLower) ||
        log.metadata?.username?.toLowerCase().includes(searchLower)
      );
      setFilteredLogs(filtered);
    }
  }, [search, logs]);

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'sent': 'badge-secondary',
      'delivered': 'badge-info',
      'opened': 'badge-warning',
      'replied': 'badge-success',
      'bounced': 'badge-error'
    };
    return statusColors[status] || 'badge-secondary';
  };

  const columns = [
    {
      key: 'dateSent',
      label: 'Date',
      width: 150,
      visible: true,
      render: (log: SentEmailLog) => (
        <span className="text-sm">
          {new Date(log.dateSent).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </span>
      )
    },
    {
      key: 'recipient',
      label: 'Recipient',
      width: 200,
      visible: true,
      render: (log: SentEmailLog) => (
        <div>
          <div className="font-medium text-sm">{log.email}</div>
          {log.recipientName && (
            <div className="text-xs text-secondary">{log.recipientName}</div>
          )}
        </div>
      )
    },
    {
      key: 'subject',
      label: 'Subject',
      width: 250,
      visible: true,
      render: (log: SentEmailLog) => (
        <span className="text-sm" title={log.subject}>
          {log.subject?.length > 50 
            ? log.subject.substring(0, 50) + '...' 
            : log.subject}
        </span>
      )
    },
    {
      key: 'template',
      label: 'Template',
      width: 150,
      visible: true,
      render: (log: SentEmailLog) => (
        <div className="text-sm">
          {log.templateId ? (
            <span>{log.templateId.name}</span>
          ) : (
            <span className="text-secondary">No template</span>
          )}
          {log.templateCategory && (
            <div className="text-xs text-secondary mt-1">
              {log.templateCategory}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      width: 100,
      visible: true,
      render: (log: SentEmailLog) => (
        <span className={`badge ${getStatusBadge(log.status)}`}>
          {log.status}
        </span>
      )
    },
    {
      key: 'platform',
      label: 'Platform',
      width: 100,
      visible: true,
      render: (log: SentEmailLog) => (
        <div>
          {log.metadata?.platform ? (
            <span className="badge badge-outline text-xs">
              {log.metadata.platform.toUpperCase()}
            </span>
          ) : (
            <span className="text-xs text-secondary">-</span>
          )}
          {log.metadata?.username && (
            <div className="text-xs text-secondary mt-1">
              @{log.metadata.username}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'source',
      label: 'Source',
      width: 100,
      visible: true,
      render: (log: SentEmailLog) => (
        <span className="text-sm capitalize">{log.source}</span>
      )
    },
    {
      key: 'emailContent',
      label: 'Email Content',
      width: 300,
      visible: true,
      render: (log: SentEmailLog) => (
        <div 
          className="cursor-pointer hover:bg-base-200 p-2 rounded"
          onClick={() => {
            setSelectedLog(log);
            setShowEmailModal(true);
          }}
        >
          <div className="text-sm line-clamp-2">
            {log.content ? log.content.replace(/<[^>]*>?/gm, '').substring(0, 150) + '...' : 'No content'}
          </div>
          <button className="btn btn-xs btn-link mt-1">View Full</button>
        </div>
      )
    }
  ];

  return (
    <div className="outreach-logs">
      <div className="page-header">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/outreach')}
            className="btn btn-secondary"
            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
            title="Back to Outreach"
          >
            <span style={{ fontSize: '18px' }}>←</span> Back
          </button>
          <h1 className="text-2xl font-semibold">Email Logs - {ruleId === 'initial-email' ? 'Initial Email' : ruleId}</h1>
          <span className="badge badge-primary">{filteredLogs.length} emails</span>
        </div>
      </div>

      {/* Email Statistics */}
      {!loading && stats && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px'
          }}>
            <div style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '10px',
              padding: '14px 16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Total Sent
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#3b82f6' }}>
                {stats.sent.toLocaleString()}
              </div>
            </div>

            <div style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '10px',
              padding: '14px 16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Delivered
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#10b981' }}>
                {stats.delivered.toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #888)' }}>
                {stats.deliveryRate}% rate
              </div>
            </div>

            <div style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '10px',
              padding: '14px 16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Opened
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#8b5cf6' }}>
                {stats.opened.toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #888)' }}>
                {stats.openRate}% rate
              </div>
            </div>

            <div style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '10px',
              padding: '14px 16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Clicked
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#f59e0b' }}>
                {stats.clicked.toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #888)' }}>
                {stats.clickRate}% rate
              </div>
            </div>

            <div style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '10px',
              padding: '14px 16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Replied
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#06b6d4' }}>
                {stats.replied.toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #888)' }}>
                {stats.replyRate}% rate
              </div>
            </div>

            <div style={{
              background: 'var(--bg-primary, #fff)',
              border: '1px solid var(--border-color, #e0e0e0)',
              borderRadius: '10px',
              padding: '14px 16px'
            }}>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #666)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Bounced
              </div>
              <div style={{ fontSize: '24px', fontWeight: '700', color: '#ef4444' }}>
                {stats.bounced.toLocaleString()}
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary, #888)' }}>
                {stats.bounceRate}% rate
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="controls-section">
        <div className="flex gap-3 flex-wrap">
          <input
            type="text"
            placeholder="Search by email, recipient, subject, status, platform, username..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input flex-1 min-w-[300px]"
          />
          
          <button
            onClick={fetchEmailLogs}
            className="btn btn-secondary"
          >
            ↻ Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading email logs...</p>
        </div>
      ) : filteredLogs.length === 0 ? (
        <div className="empty-state">
          <p className="text-secondary">No email logs found</p>
          {search && (
            <p className="text-sm">Try adjusting your search criteria</p>
          )}
        </div>
      ) : (
        <DataTable
          data={filteredLogs}
          columns={columns}
          idField="_id"
        />
      )}

      <style>{`
        .outreach-logs {
          padding: 20px;
        }
        
        .page-header {
          margin-bottom: 24px;
        }
        
        .controls-section {
          margin-bottom: 20px;
        }
        
        .loading-state, .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }
        
        .spinner {
          width: 40px;
          height: 40px;
          border: 3px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 16px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .logs-table {
          background: var(--background);
          border-radius: 8px;
          overflow: hidden;
        }
      `}</style>

      {/* Email Content Modal */}
      {showEmailModal && selectedLog && createPortal(
        <div className="modal-overlay" onClick={() => setShowEmailModal(false)}>
          <div 
            className="modal-content" 
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}
          >
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Email Details</h3>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  setShowEmailModal(false);
                  setSelectedLog(null);
                }}
              >
                ✕
              </button>
            </div>
            
            <div className="modal-body" style={{ padding: '20px' }}>
              {/* Email Metadata */}
              <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    From
                  </label>
                  <div style={{ fontWeight: '500' }}>{selectedLog.senderName || 'N/A'}</div>
                  <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                    {selectedLog.senderEmail || 'N/A'}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    To
                  </label>
                  <div style={{ fontWeight: '500' }}>{selectedLog.recipientName || selectedLog.email}</div>
                  {selectedLog.recipientName && (
                    <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                      {selectedLog.email}
                    </div>
                  )}
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Date Sent
                  </label>
                  <div style={{ fontWeight: '500' }}>
                    {new Date(selectedLog.dateSent).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Status
                  </label>
                  <span className={`badge ${getStatusBadge(selectedLog.status)}`}>
                    {selectedLog.status}
                  </span>
                </div>

                <div>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                    Template
                  </label>
                  <div style={{ fontWeight: '500' }}>
                    {selectedLog.templateId?.name || 'No template'}
                  </div>
                </div>
              </div>
              
              {/* Subject */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                  Subject
                </label>
                <div style={{ 
                  padding: '12px', 
                  backgroundColor: 'var(--bg-secondary)', 
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  {selectedLog.subject}
                </div>
              </div>
              
              {/* Content */}
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                  Content
                </label>
                <div style={{ 
                  padding: '16px', 
                  backgroundColor: 'var(--bg-secondary)', 
                  borderRadius: '8px',
                  minHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  lineHeight: '1.6'
                }}>
                  {selectedLog.content.includes('<') ? (
                    <div dangerouslySetInnerHTML={{ __html: selectedLog.content }} />
                  ) : (
                    selectedLog.content
                  )}
                </div>
              </div>
              
              {/* Platform info if available */}
              {selectedLog.metadata?.platform && (
                <div style={{ marginTop: '20px', padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '8px' }}>
                  <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
                    Platform Details
                  </label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Platform: </span>
                      <span className="badge" style={{ marginLeft: '8px' }}>
                        {selectedLog.metadata.platform}
                      </span>
                    </div>
                    {selectedLog.metadata.username && (
                      <div>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Username: </span>
                        <span style={{ fontWeight: '500' }}>@{selectedLog.metadata.username}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setSelectedLog(null);
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OutreachLogs;